import DnsIcon from '@mui/icons-material/Dns';
import EmailIcon from '@mui/icons-material/Email';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GroupIcon from '@mui/icons-material/Group';
import SportsIcon from '@mui/icons-material/Sports';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import { Box, Card, Chip, Typography, useTheme } from '@mui/material';
import React from 'react';
import type { HowItWorksRole, HowItWorksStep, HowItWorksStepType } from '../../types/howItWorks';

const ROLE_ICONS: Record<HowItWorksRole, React.ReactNode> = {
  coach: <GroupIcon sx={{ fontSize: 14 }} />,
  athlete: <SportsIcon sx={{ fontSize: 14 }} />,
  guardian: <FamilyRestroomIcon sx={{ fontSize: 14 }} />,
  system: <DnsIcon sx={{ fontSize: 14 }} />,
};

const TYPE_CONFIG: Record<HowItWorksStepType, { label: string; icon: React.ReactNode; borderColor: string }> = {
  UI: { label: 'UI', icon: <WebIcon sx={{ fontSize: 14 }} />, borderColor: 'primary.main' },
  Notification: { label: 'Notification', icon: <EmailIcon sx={{ fontSize: 14 }} />, borderColor: 'info.main' },
  backend: { label: 'Backend', icon: <StorageIcon sx={{ fontSize: 14 }} />, borderColor: 'grey.500' },
};

export interface StepBubbleProps {
  step: HowItWorksStep;
  selected: boolean;
  onClick: () => void;
}

const StepBubble: React.FC<StepBubbleProps> = ({ step, selected, onClick }) => {
  const theme = useTheme();
  const typeConfig = TYPE_CONFIG[step.type];

  return (
    <Card
      component="button"
      type="button"
      onClick={onClick}
      variant={selected ? 'elevation' : 'outlined'}
      elevation={selected ? 2 : 0}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        p: 1.5,
        cursor: 'pointer',
        borderRadius: 2,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: typeConfig.borderColor,
        bgcolor: selected ? (theme.palette.mode === 'dark' ? 'action.selected' : 'action.hover') : 'background.paper',
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'action.selected',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Chip
          size="small"
          icon={typeConfig.icon as React.ReactElement}
          label={typeConfig.label}
          sx={{ height: 20, '& .MuiChip-icon': { mr: 0.25 } }}
        />
        {step.roles.map((r) => (
          <Chip
            key={r}
            size="small"
            icon={ROLE_ICONS[r] as React.ReactElement}
            label={r}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-icon': { mr: 0.25 } }}
          />
        ))}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {step.label}
      </Typography>
    </Card>
  );
};

export default StepBubble;
